import { Component, Input } from '@angular/core';
import { CarrierSync } from '../../models/carrier-sync.model';
import { TruckJourneyService } from '../../services/truck-journey.service';
import { EMPTY, iif, interval, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { CarrierService } from '../../../../user-dashboard/shared/carrier.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';

/**
 * This component has for sole purpose to display a truck journey synchronization button, and the state of the
 * last and current synchronization
 */
@Component({
  selector: 'app-carrier-sync',
  templateUrl: './carrier-sync.component.html',
  styleUrls: ['./carrier-sync.component.scss']
})
export class CarrierSyncComponent {

  public lastSync: CarrierSync = null;
  public isButtonVisible = false;
  private _carrierId: string = null;
  private POLLING_INTERVAL_MS = 3000;
  private TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes in milliseconds
  private MAX_ATTEMPTS = this.TIMEOUT_MS / this.POLLING_INTERVAL_MS;

  @Input() private set carrierId(carrierId: string) {
      this._carrierId = carrierId;
      this.updateDisplay();
  }

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private truckJourneyService: TruckJourneyService,
    private carrierService: CarrierService,
    private authService: AuthenticationService
  ) {}

  public syncTruckJourneys(): void {
    this.lastSync = {status: 'IN_PROGRESS', endedAt: undefined, hasEnded: () => false};
    this.truckJourneyService.synchronizeCarrier(this._carrierId)
      .pipe(
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'truck-journeys.errors.carrier-sync');
          return EMPTY;
        }),
        switchMap(() => this.pollCarrierSyncUntilFinished())
      )
      .subscribe();
  }

  public updateDisplay() {
    this.isButtonVisible = false;
    this.lastSync = null;

    iif(
      () => this.authService.canManageGlobalTruckJourneys() && this._carrierId != null,
      this.carrierService.getById(this._carrierId),
      this.authService.userProfileBehavior().asObservable()
    ).pipe(map(profile => profile?.featureProfile?.tmsLoadsSynchronization)).subscribe(hasTmsSync => {
      this.isButtonVisible = hasTmsSync != null ? !hasTmsSync : false;
      if (this.isButtonVisible) {
        this.getLastSync();
      }
    });
  }

  /**
   * Poll every 3 secs if the carrier sync has finished or not. Stops polling after 5 mins.
   */
  private pollCarrierSyncUntilFinished(): Observable<CarrierSync> {
    return interval(this.POLLING_INTERVAL_MS)
      .pipe(
        take(this.MAX_ATTEMPTS),
        switchMap(() => this.truckJourneyService.getCarrierLastSyncResult(this._carrierId)),
        takeWhile(carrierSync => !carrierSync || !carrierSync.hasEnded(), true),
        tap(lastSync => this.lastSync = lastSync),
        catchError((e: HttpErrorResponse) => {
          this.errorHandlerService.handleHttpError(e, 'truck-journeys.errors.carrier-sync-result');
          return EMPTY;
        }),
      );
  }

  private getLastSync(): void {
    this.truckJourneyService.getCarrierLastSyncResult(this._carrierId)
        .pipe(
          catchError((e: HttpErrorResponse) => {
            this.errorHandlerService.handleHttpError(e, 'truck-journeys.errors.carrier-sync-result');
            return EMPTY;
          }),
          tap((carrierSync: CarrierSync) => {
            if (carrierSync && !carrierSync.hasEnded()) {
              this.lastSync = null;
              this.pollCarrierSyncUntilFinished();
            }
          }),
          filter(carrierSync => carrierSync?.hasEnded())
        )
        .subscribe(carrierSync => this.lastSync = carrierSync);
  }
}
